import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Settings as SettingsIcon, Plus, Trash2, Save, CalendarClock, Star, Info, ShieldCheck } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { getBookingSettings, saveBookingSettings, getCaptchaSettings, saveCaptchaSettings } from '../lib/marketingApi';

const PROVIDERS = [
  { value: 'microsoft_bookings', label: 'Microsoft Bookings' },
  { value: 'calendly', label: 'Calendly' },
  { value: 'other', label: 'Other' },
];
const NEW_TEAM = () => ({ name: '', provider: 'microsoft_bookings', scheduler_url: '', is_default: false, active: true, members: [] });
const NEW_MEMBER = () => ({ name: '', email: '', role: '', active: true, booking_url: '' });

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [zoomUrl, setZoomUrl] = useState('');
  const [instructions, setInstructions] = useState('');
  const [cap, setCap] = useState({ provider: 'none', site_key: '', secret_key: '', enabled: false });
  const [secretSet, setSecretSet] = useState(false);
  const [savingCap, setSavingCap] = useState(false);

  useEffect(() => {
    getBookingSettings().then((d) => {
      const s = d.settings || {};
      setTeams(s.teams || []); setZoomUrl(s.zoom_url || ''); setInstructions(s.instructions || '');
    }).catch(() => toast.error('Failed to load settings')).finally(() => setLoading(false));
    getCaptchaSettings().then((d) => {
      setCap({ provider: d.provider || 'none', site_key: d.site_key || '', secret_key: '', enabled: !!d.enabled });
      setSecretSet(!!d.secret_set);
    }).catch(() => {});
  }, []);

  const saveCaptcha = async () => {
    setSavingCap(true);
    try {
      const payload = { provider: cap.provider, site_key: cap.site_key, enabled: cap.enabled };
      if (cap.secret_key) payload.secret_key = cap.secret_key;  // omit → keep existing
      const d = await saveCaptchaSettings(payload);
      setSecretSet(secretSet || !!cap.secret_key);
      setCap((c) => ({ ...c, secret_key: '' }));
      toast.success(d.active ? 'Anti-spam enabled' : 'Anti-spam settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingCap(false); }
  };

  const updateTeam = (i, patch) => setTeams((ts) => ts.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const setDefault = (i) => setTeams((ts) => ts.map((t, idx) => ({ ...t, is_default: idx === i })));
  const removeTeam = (i) => setTeams((ts) => ts.filter((_, idx) => idx !== i));
  const addTeam = () => setTeams((ts) => [...ts, { ...NEW_TEAM(), is_default: ts.length === 0 }]);
  const updateMember = (ti, mi, patch) => setTeams((ts) => ts.map((t, idx) => idx === ti ? { ...t, members: t.members.map((m, j) => j === mi ? { ...m, ...patch } : m) } : t));
  const addMember = (ti) => setTeams((ts) => ts.map((t, idx) => idx === ti ? { ...t, members: [...(t.members || []), NEW_MEMBER()] } : t));
  const removeMember = (ti, mi) => setTeams((ts) => ts.map((t, idx) => idx === ti ? { ...t, members: t.members.filter((_, j) => j !== mi) } : t));

  const save = async () => {
    setSaving(true);
    try {
      const d = await saveBookingSettings({ teams, zoom_url: zoomUrl, instructions });
      const s = d.settings || {};
      setTeams(s.teams || []); setZoomUrl(s.zoom_url || ''); setInstructions(s.instructions || '');
      toast.success('Booking settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      <AdminHeader active="settings" actions={(
        <button onClick={save} disabled={saving || loading} data-testid="settings-save-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      )} />
      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><SettingsIcon className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Settings</h1>
            <p className="text-sm text-slate-500">Booking &amp; scheduling — teams and their round-robin scheduler links</p>
          </div>
        </div>

        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-sky-200 bg-sky-50 p-3.5 text-sm text-sky-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Availability lives in your scheduler (Microsoft Bookings / Calendly). Add staff to that team's booking page there — their Outlook/Teams calendars sync automatically and round-robin across them. Paste the resulting public link below.</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0035A8]" /></div>
        ) : (
          <div className="space-y-4" data-testid="booking-teams">
            {teams.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400" data-testid="teams-empty">
                No booking teams yet. Add your first team (e.g. Presales or Sales Support).
              </div>
            )}
            {teams.map((t, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`booking-team-${i}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><CalendarClock className="h-4 w-4" /></span>
                  <input value={t.name} onChange={(e) => updateTeam(i, { name: e.target.value })} placeholder="Team name (e.g. Presales)" data-testid={`team-name-${i}`} className={`flex-1 min-w-[160px] ${inputCls}`} />
                  <select value={t.provider} onChange={(e) => updateTeam(i, { provider: e.target.value })} data-testid={`team-provider-${i}`} className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm">
                    {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                  <button onClick={() => setDefault(i)} data-testid={`team-default-${i}`} title="Set as default"
                    className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold ${t.is_default ? 'bg-amber-100 text-amber-700' : 'text-slate-400 hover:bg-slate-100'}`}>
                    <Star className={`h-3.5 w-3.5 ${t.is_default ? 'fill-amber-500 text-amber-500' : ''}`} /> {t.is_default ? 'Default' : 'Make default'}
                  </button>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                    <input type="checkbox" checked={t.active !== false} onChange={(e) => updateTeam(i, { active: e.target.checked })} data-testid={`team-active-${i}`} /> Active
                  </label>
                  <button onClick={() => removeTeam(i)} data-testid={`team-remove-${i}`} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Scheduler link (round-robin URL)</label>
                  <input value={t.scheduler_url} onChange={(e) => updateTeam(i, { scheduler_url: e.target.value })} placeholder="https://outlook.office365.com/book/…  or  https://calendly.com/…/30min" data-testid={`team-url-${i}`} className={inputCls} />
                </div>

                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Team members (roster)</span>
                    <button onClick={() => addMember(i)} data-testid={`team-add-member-${i}`} className="text-xs font-semibold text-[#0035A8] hover:underline">+ Add member</button>
                  </div>
                  {(t.members || []).length === 0 ? (
                    <p className="text-xs text-slate-400">Add the staff on this team's rota (for visibility; calendars are connected in the scheduler tool).</p>
                  ) : (
                    <div className="space-y-1.5">
                      {t.members.map((m, mi) => (
                        <div key={mi} className="grid grid-cols-12 gap-1.5" data-testid={`team-member-${i}-${mi}`}>
                          <input value={m.name} onChange={(e) => updateMember(i, mi, { name: e.target.value })} placeholder="Name" className={`col-span-3 ${inputCls}`} />
                          <input value={m.email} onChange={(e) => updateMember(i, mi, { email: e.target.value })} placeholder="Email" className={`col-span-3 ${inputCls}`} />
                          <input value={m.role} onChange={(e) => updateMember(i, mi, { role: e.target.value })} placeholder="Role" className={`col-span-3 ${inputCls}`} />
                          <input value={m.booking_url || ''} onChange={(e) => updateMember(i, mi, { booking_url: e.target.value })} placeholder="Personal link (opt.)" className={`col-span-2 ${inputCls}`} />
                          <button onClick={() => removeMember(i, mi)} className="col-span-1 flex items-center justify-center text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button onClick={addTeam} data-testid="add-team-btn" className="inline-flex items-center gap-1.5 rounded-md border border-[#0035A8] px-3.5 py-2 text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8] hover:text-white">
              <Plus className="h-4 w-4" /> Add booking team
            </button>

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-3 text-sm font-semibold text-slate-800">Shared options</div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Zoom link (optional — for calls run over Zoom)</label>
              <input value={zoomUrl} onChange={(e) => setZoomUrl(e.target.value)} placeholder="https://zoom.us/j/…" data-testid="settings-zoom-url" className={`${inputCls} mb-3`} />
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Booking page instructions (shown on /book-a-call)</label>
              <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} placeholder="e.g. Pick a 30-minute slot that suits you and we'll walk you through your portfolio." data-testid="settings-instructions" className={inputCls} />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="captcha-settings">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <ShieldCheck className="h-4 w-4 text-[#0035A8]" /> Spam shield (CAPTCHA)
              </div>
              <p className="mb-4 text-xs text-slate-500">Protect the public support form from bots. Choose a provider and paste your keys, then enable it.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Provider</label>
                  <select value={cap.provider} onChange={(e) => setCap({ ...cap, provider: e.target.value })} data-testid="captcha-provider" className="h-10 w-full rounded-md border border-slate-300 bg-white px-2.5 text-sm">
                    <option value="none">None (off)</option>
                    <option value="turnstile">Cloudflare Turnstile</option>
                    <option value="recaptcha">Google reCAPTCHA v2</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Site key</label>
                  <input value={cap.site_key} onChange={(e) => setCap({ ...cap, site_key: e.target.value })} placeholder="Public site key" data-testid="captcha-site-key" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Secret key {secretSet && <span className="text-emerald-600">· saved</span>}</label>
                  <input value={cap.secret_key} onChange={(e) => setCap({ ...cap, secret_key: e.target.value })} type="password" placeholder={secretSet ? '•••••• (leave blank to keep)' : 'Secret key'} data-testid="captcha-secret-key" className={inputCls} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={cap.enabled} onChange={(e) => setCap({ ...cap, enabled: e.target.checked })} data-testid="captcha-enabled" /> Enable on the support form
                </label>
                <button onClick={saveCaptcha} disabled={savingCap} data-testid="captcha-save-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                  {savingCap ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save spam shield
                </button>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 p-3 text-xs text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {cap.provider === 'turnstile' && 'Get free keys at dash.cloudflare.com → Turnstile → Add site. Copy the Site key and Secret key here.'}
                  {cap.provider === 'recaptcha' && 'Get keys at google.com/recaptcha/admin → register a v2 “I’m not a robot” site. Copy the Site key and Secret key here.'}
                  {cap.provider === 'none' && 'Anti-spam is off. Honeypot + rate-limiting still protect the form. Pick a provider above to add a visible challenge.'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
